/**
 * The last few things this browser searched for.
 *
 * ## Why it is not on the server
 *
 * It could be: there is a profile, and the server already stores watch history against it.
 * It is not, for two reasons that both point the same way. A search is a keystroke-level
 * record of what somebody was looking for, which is a more intimate thing to keep than
 * what they finished watching, and this is a shared household account — syncing it would
 * put one person's half-typed searches on another person's screen. And it would cost an
 * endpoint, a collection and a migration to make a list of six strings survive a browser
 * change, which nobody has asked for.
 *
 * So: `localStorage`, per browser, per profile key, and it never leaves the device.
 *
 * ## Why every function is defensive
 *
 * `localStorage` throws rather than returns null in two ordinary cases — Safari's private
 * mode, and any browser at its storage quota — and the packaged widget runs from a
 * `file://` origin where the whole API can be absent. A search box that throws on mount
 * because it could not read a convenience list is a screen that does not open, so every
 * access here fails to "no recents" instead.
 */

const KEY = 'filmodasi:recent-searches';

/** Six: enough to be useful, few enough to stay one line on a phone. */
export const MAX_RECENTS = 6;

/** Below this a query is not a search anybody meant to keep. */
const MIN_LENGTH = 2;

function read(): string[] {
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    // Anything at all could be under this key — an older format, or another tab's bug.
    // Filtering to strings is cheaper than versioning and cannot throw.
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((x): x is string => typeof x === 'string').slice(0, MAX_RECENTS);
  } catch {
    return [];
  }
}

function write(list: string[]): void {
  try {
    window.localStorage.setItem(KEY, JSON.stringify(list));
  } catch {
    /* Full, or unavailable. The list is a convenience; losing it costs nothing. */
  }
}

export function recentSearches(): string[] {
  return read();
}

/**
 * Records a query, most recent first.
 *
 * Case- and whitespace-insensitive de-duplication, because "batman", "Batman " and
 * "BATMAN" are one search to the person who typed them and three entries to a `Set`. The
 * casing that survives is the newest, which is the one they just chose to type.
 */
export function rememberSearch(query: string): string[] {
  const trimmed = query.trim().replace(/\s+/g, ' ');
  if (trimmed.length < MIN_LENGTH) return read();

  const key = trimmed.toLocaleLowerCase('tr');
  const next = [trimmed, ...read().filter((q) => q.toLocaleLowerCase('tr') !== key)].slice(
    0,
    MAX_RECENTS,
  );
  write(next);
  return next;
}

export function forgetSearch(query: string): string[] {
  const key = query.trim().toLocaleLowerCase('tr');
  const next = read().filter((q) => q.toLocaleLowerCase('tr') !== key);
  write(next);
  return next;
}

export function clearSearches(): string[] {
  write([]);
  return [];
}
